import { useInfiniteQuery } from '@tanstack/react-query';

import { getTendersWithInteraction } from '../../../shared/api/magellan/interaction';
import type { DecisionStatus } from '../../../shared/entities/Interaction';
import type { TenderStatus } from '../../../shared/entities/Tender';
import { parseFrLocaleDateStringToDate } from '../../../shared/utils/dates';
import { useGlobalSearchFilter } from './useGlobalSearchFilter.hook';
import { usePipelineFilters } from './usePipelineFilters.hook';
import { useTabsStatus } from './useTabs.hook';

export const RESULTS_PER_PAGE = 30;

export function useTendersWithInteractionQuery() {
  const { filters, isLoaded: isFiltersLoaded } = usePipelineFilters();
  const { globalSearchFilter } = useGlobalSearchFilter();
  const { activeTabStatus } = useTabsStatus();

  const params = buildParams({
    decisionStatus: activeTabStatus,
    globalSearchFilter,
    ...filters,
  });
  const queryKey = [getTendersWithInteraction.name, params];

  const query = useInfiniteQuery({
    enabled: isFiltersLoaded,
    initialPageParam: 0,
    queryKey,
    queryFn: ({ pageParam }) =>
      getTendersWithInteraction({ ...params, skip: pageParam }),
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const { skip, totalResults, take } = lastPage.pagination;
      return skip + take < totalResults
        ? lastPageParam + RESULTS_PER_PAGE
        : null;
    },
  });

  const onNoteChange = () => {
    query.refetch();
  };

  return { ...query, onNoteChange };
}

export function buildParams({
  decisionStatus,
  globalSearchFilter,
  responseDeadline,
  status,
  owner,
}: {
  decisionStatus?: DecisionStatus;
  globalSearchFilter?: string;
  responseDeadline?: string[];
  status?: TenderStatus[];
  owner?: string[];
}) {
  return {
    decisionStatus,
    ownerId: owner?.join(','),
    q: globalSearchFilter,
    responseDeadline:
      responseDeadline && parseFrLocaleDateStringToDate(responseDeadline[0]),
    tenderStatus: status?.join(',') as TenderStatus,
    take: RESULTS_PER_PAGE,
  };
}
